/**
 * EU Regulation 1169/2011 compliant rounding for nutrition label values.
 * Based on the Guidance Document rounding rules (Table 4).
 */

import type { TFunction } from "i18next";
import type { NutritionProfileFragment } from "../gql/generated/graphqlSchema.tsx";

export type NutrientType =
  "energy" | "fat" | "carbohydrate" | "sugars" | "protein" | "fibre" | "saturatedFat" | "salt" | "alcohol";

/** Standard nutrients: fat, carbohydrate, sugars, protein, fibre */
type StandardNutrient = "fat" | "carbohydrate" | "sugars" | "protein" | "fibre" | "alcohol";

const isStandardNutrient = (type: NutrientType): type is StandardNutrient =>
  type === "fat" ||
  type === "carbohydrate" ||
  type === "sugars" ||
  type === "protein" ||
  type === "fibre" ||
  type === "alcohol";

export const formatNumber = (value: number, language: string, decimals?: number) =>
  new Intl.NumberFormat(
    language,
    decimals != null ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals } : undefined,
  ).format(value);

const formatStandard = (value: number, language: string): string => {
  if (value === 0) return "0";
  if (value <= 0.5) return `< ${formatNumber(0.5, language, 1)}`;
  if (value >= 10) return formatNumber(value, language, 0);
  return formatNumber(value, language, 1);
};

const formatSaturatedFat = (value: number, language: string): string => {
  if (value === 0) return "0";
  if (value <= 0.1) return `< ${formatNumber(0.1, language, 1)}`;
  if (value >= 10) return formatNumber(value, language, 0);
  return formatNumber(value, language, 1);
};

const formatSalt = (value: number, language: string): string => {
  if (value === 0) return "0";
  if (value <= 0.0125) return `< ${formatNumber(0.01, language, 2)}`;
  if (value >= 1) return formatNumber(value, language, 1);
  return formatNumber(value, language, 2);
};

/**
 * Formats a nutrient value according to EU Regulation 1169/2011 rounding rules.
 *
 * @param language — BCP 47 language tag (e.g. "en", "bg") for locale decimal separator.
 * @returns The formatted value string, or `null` if the input is null/undefined.
 *          Does NOT include the unit suffix (g, kJ, kcal) — the caller appends it.
 */
export const formatNutrient = (
  value: number | null | undefined,
  type: NutrientType,
  language: string = "en",
): string | null => {
  if (value == null) return null;

  const n = value;

  if (type === "energy") return formatNumber(n, language, 0);
  if (isStandardNutrient(type)) return formatStandard(n, language);
  if (type === "saturatedFat") return formatSaturatedFat(n, language);
  return formatSalt(n, language);
};

/**
 * Formats a nutrition profile as a single-line EU 1169/2011-style declaration
 * suitable for copy-pasting onto a food label, e.g.:
 *
 *   "Nutrition information per 100g: energy 616kJ/147kcal, fat 7.1g, of which
 *    saturates 4.3g, carbohydrate 15g, of which sugars 5.7g, protein 4.6g, salt 0.06g"
 *
 * Null values are skipped. Fibre is only emitted when present. Returns `null`
 * if the profile has no usable data (no energy value at all).
 */
export const formatNutritionDeclaration = (
  np: NutritionProfileFragment | null | undefined,
  language: string,
  t: TFunction,
): { header: string; body: string } | null => {
  if (np == null) return null;
  if (np.energyKj == null && np.energyKcal == null) return null;

  const parts: string[] = [];

  const kj = formatNutrient(np.energyKj, "energy", language);
  const kcal = formatNutrient(np.energyKcal, "energy", language);
  const energyValue = kj != null && kcal != null ? `${kj}kJ/${kcal}kcal` : kj != null ? `${kj}kJ` : `${kcal}kcal`;
  parts.push(`${t("nutrition.declaration.energy")} ${energyValue}`);

  const fat = formatNutrient(np.fat, "fat", language);
  if (fat != null) parts.push(`${t("nutrition.declaration.fat")} ${fat}g`);

  const saturates = formatNutrient(np.saturatedFat, "saturatedFat", language);
  if (saturates != null) parts.push(`${t("nutrition.declaration.saturatedFat")} ${saturates}g`);

  const carbs = formatNutrient(np.carbohydrate, "carbohydrate", language);
  if (carbs != null) parts.push(`${t("nutrition.declaration.carbohydrate")} ${carbs}g`);

  const sugars = formatNutrient(np.sugars, "sugars", language);
  if (sugars != null) parts.push(`${t("nutrition.declaration.sugars")} ${sugars}g`);

  if (np.fibre != null) {
    const fibre = formatNutrient(np.fibre, "fibre", language);
    if (fibre != null) parts.push(`${t("nutrition.declaration.fibre")} ${fibre}g`);
  }

  const protein = formatNutrient(np.protein, "protein", language);
  if (protein != null) parts.push(`${t("nutrition.declaration.protein")} ${protein}g`);

  const salt = formatNutrient(np.salt, "salt", language);
  if (salt != null) parts.push(`${t("nutrition.declaration.salt")} ${salt}g`);

  return { header: t("nutrition.declaration.header"), body: parts.join(", ") };
};
